import React, { useEffect, useState } from 'react'
import { useUser } from 'util/UserContext'
import api from 'config/axiosConfig'
import SpillTheTeaModal from 'components/Posts/SpillTheTeaModal'
import SolutionCardRefactored from './SolutionCardRefactored'
import AddButton from 'ui/AddButton'

import { Issue, Solution } from 'util/constants'
import LoadingSkeleton from 'ui/LoadingSkeleton'
import NotFoundPage from 'components/ResponsePages/404NotFoundPage'
import { AxiosError } from 'axios'
import ServerErrorPage from 'components/ResponsePages/500ServerErrorPage'
import ScrollHideContainer from 'ui/ScrollHideContainer'
import EmptyState from 'ui/EmptyState'
import { apiRoutes } from "util/apiRoutes";

interface Props {
  issue: Issue
}

const SolutionsSection = ({ issue }: Props) => {
  const { currentUser, setShowAuthModal } = useUser()
  const [solutions, setSolutions] = useState<Solution[]>([])
  const [error, setError] = useState('')
  const [openModal, setOpenModal] = useState(false)

  useEffect(() => {
    const fetchSolutions = async () => {
      try {
        const response = await api.get<Solution[]>(
          apiRoutes.posts.root, { params: { kind: "solution", issueId: issue.id } }
        )
        setSolutions(response.data)
      } catch (err) {
        if (err instanceof AxiosError && err.response?.status === 404) {
            setError('404: Failed to load solutions. Please try again later.')
        } else {
            setError('Failed to load solutions. Please try again later.')
        }
      }
    }

    if (issue) {
      fetchSolutions()
    }
  }, [issue])

  const handleSolutionAdded = async () => {
    try {
      const response = await api.get<Solution[]>(
        apiRoutes.posts.root, { params: { kind: "solution", issueId: issue.id } }
      )
      setSolutions(response.data)
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status === 404) {
        setError('404: Failed to load solutions. Please try again later.')
      } else {
        setError('Failed to load solutions. Please try again later.')
      }
    }
  }

  const handleOpenModal = () => {
    if (!currentUser) {
      setShowAuthModal(true)
    } else {
      setOpenModal(true)
    }
  }

  const handleCloseModal = () => {
    setOpenModal(false)
  }

  if (error) {
    return error.match(/404/) ? <NotFoundPage displayStyle={`page`} errorMessage={`Sorry, we couldn't find any solutions for this issue.`} buttonText={`View Other Solutions`} buttonHref={`/solutions/new`} /> : <ServerErrorPage />
  }

  if (!issue) {
    return (
      <LoadingSkeleton variant="entity-card" count={5} />
    )
  }

  if (!solutions) {
    return (
      <LoadingSkeleton variant="entity-card" count={5} />
    )
  }

  return (
    <div className="solutions-section pb-6">
      <ScrollHideContainer 
        className="z-50 max-w-5xl bg-eoe-bg dark:bg-eoe-dark-bg"
        hideOffset={115}
        threshold={114}
      >
      <div className="flex flex-col space-y-1 mb-1">
        <div className="flex mb-1 mx-1">
          <AddButton
            tooltipTitle="Add a solution"
            buttonText="Solution"
            onClick={handleOpenModal}
            className="mx-2 w-full"
          />
        </div>
      </div>
      </ScrollHideContainer>
      {openModal && (
        <SpillTheTeaModal
          open={openModal}
          onClose={handleCloseModal}
          onSolutionAdded={handleSolutionAdded}
          initialSelectedIssue={issue}
          customTitle="Share a solution"
          fixedPostType="solution"
          hidePostTypeButtons={true}
        />
      )}
      <div className="entity-divider">
        {solutions.length === 0 ? (
          <EmptyState
            title="No solutions yet"
            subtitle="Be the first to propose a solution for this issue!"
            className="m-4"
          />
        ) : (
          solutions.map((solution) => (
            <SolutionCardRefactored
              key={`${solution.id}`}
              solution={solution}
              truncateDescription={true}
              isEntityProfilePage={false}
              isEntitySection={true}
            />
          ))
        )}
      </div>
    </div>
  )
}

export default SolutionsSection
